<template>
    <div>
        <button
            type="button"
            data-test="play-review-video"
            class="relative block w-full overflow-hidden rounded-md focus:outline-none"
            @click="openPlayer"
        >
            <span class="sr-only"
                >Play the video submitted with this review</span
            >

            <span
                class="aspect-w-1 aspect-h-1 block overflow-hidden rounded-md bg-gray-100"
            >
                <!-- Generic alt text on purpose: describing a customer's recording would put a
                     claim about results in words nobody wrote. -->
                <img
                    :src="posterUrl"
                    alt="Video submitted with this review"
                    class="h-full w-full object-contain"
                    loading="lazy"
                />
            </span>

            <span
                class="absolute inset-0 flex items-center justify-center"
                aria-hidden="true"
            >
                <svg
                    class="h-16 w-16 text-nutreance-green"
                    fill="currentColor"
                    viewBox="0 -1 84 86"
                >
                    <circle
                        class="stroke fill-gray-100 stroke-gray-300 hover:fill-gray-200"
                        opacity="0.9"
                        cx="42"
                        cy="42"
                        r="42"
                    ></circle>
                    <path
                        d="M55.5039 40.3359L37.1094 28.0729C35.7803 27.1869 34 28.1396 34 29.737V54.263C34 55.8604 35.7803 56.8131 37.1094 55.9271L55.5038 43.6641C56.6913 42.8725 56.6913 41.1275 55.5039 40.3359Z"
                    ></path>
                </svg>
            </span>
        </button>

        <ReviewDialog
            :open="open"
            panel-class="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:p-6"
            @close="open = false"
            @after-leave="disposePlayer"
        >
            <div class="absolute right-0 top-0 pr-4 pt-4">
                <button
                    type="button"
                    data-test="close-review-video"
                    class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                    @click="open = false"
                >
                    <span class="sr-only">Close</span>
                    <XIcon class="h-6 w-6" aria-hidden="true" />
                </button>
            </div>

            <div class="relative mt-6">
                <video
                    ref="video"
                    data-test="review-video"
                    class="video-js vjs-default-skin vjs-big-play-centered"
                    role="region"
                    aria-label="video player"
                />
            </div>
        </ReviewDialog>
    </div>
</template>

<script setup lang="ts">
import videojs from "video.js";
import type Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import { nextTick, onBeforeUnmount, ref, shallowRef } from "vue";
import { XIcon } from "@heroicons/vue/outline";
import ReviewDialog from "./ReviewDialog.vue";

const props = defineProps<{
    /** The still Cloudflare Stream serves for this clip, shown until somebody asks to watch it. */
    posterUrl: string;
    /** The HLS manifest the clip streams from. */
    hlsUrl: string;
}>();

const open = ref(false);
const video = ref<HTMLVideoElement | null>(null);

// A video.js player is a large object graph with its own listeners, and none of it belongs in
// Vue's reactivity.
const player = shallowRef<Player | null>(null);

/**
 * Open the dialog and build a player inside it.
 *
 * The player exists only while the dialog does: a reviews index can carry ten of these, and ten
 * inline players would be ten streaming engines where a poster and one modal is enough.
 */
function openPlayer(): void {
    open.value = true;

    nextTick(() => {
        if (video.value === null) {
            return;
        }

        player.value = videojs(video.value, {
            controls: true,
            preload: "none",
            // The square the poster already occupies, so the card holds its shape when the dialog
            // opens and again when the clip reports its own dimensions. Nothing is cropped to fill
            // it: on a face recorded on a phone, portrait is as likely as landscape.
            fluid: true,
            aspectRatio: "1:1",
            poster: props.posterUrl,
            // One HLS source plays everywhere from this project's Safari 13 floor up — natively
            // in Safari, through video.js's bundled HTTP streaming elsewhere.
            sources: [{ src: props.hlsUrl, type: "application/x-mpegURL" }],
        });
    });
}

/**
 * Tear the player down once the dialog has finished closing, and again on unmount.
 *
 * Unmount matters too: a listing mounts one of these per review, and a player that outlives its
 * dialog keeps a media element and its timers alive with nothing left to show them in.
 */
function disposePlayer(): void {
    player.value?.dispose();
    player.value = null;
}

onBeforeUnmount(disposePlayer);
</script>
